import type { PaymentCard } from "@app/types/checkout-types";

const DEFAULT_FORMAT = /(\d{1,4})/g;

const cards: Array<PaymentCard> = [
  {
    type: "hipercard",
    pattern: /^(606282\d{10}(\d{3})?)/,
    format: DEFAULT_FORMAT,
    length: [13, 16, 19],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "hipercard",
    pattern: /^(3841)/,
    format: DEFAULT_FORMAT,
    length: [19],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "elo",
    pattern:
      /^((((636368)|(438935)|(504175)|(451416)|(636297))\d{0,10})|((5067)|(4576)|(4011))\d{0,12})$/,
    format: DEFAULT_FORMAT,
    length: [16],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "maestro",
    pattern: /^(5(018|0[23]|[68])|6(39|7))/,
    format: DEFAULT_FORMAT,
    length: [12, 13, 14, 15, 16, 17, 18, 19],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "visa",
    pattern: /^4/,
    format: DEFAULT_FORMAT,
    length: [13, 16],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "master",
    pattern: /^((5[0-5])|(2[2-7]))/,
    format: DEFAULT_FORMAT,
    length: [16],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "american_express",
    pattern: /^3[4,7]\d{13}/,
    format: /(\d{1,4})(\d{1,6})?(\d{1,5})?/,
    length: [15],
    cvcLength: [4],
    luhn: true
  },
  {
    type: "diners",
    pattern: /^3[0689]\d{12}/,
    format: /(\d{1,4})(\d{1,6})?(\d{1,4})?/,
    length: [14],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "discover",
    pattern: /^6([045]|22)/,
    format: DEFAULT_FORMAT,
    length: [16],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "china_union_pay",
    pattern: /^(62|88)/,
    format: DEFAULT_FORMAT,
    length: [16, 17, 18, 19],
    cvcLength: [3],
    luhn: false
  },
  {
    type: "jcb",
    pattern: /^35/,
    format: DEFAULT_FORMAT,
    length: [16],
    cvcLength: [3],
    luhn: true
  }
];

const formatter = {
  getCardFromNumber: function (card_number: string): PaymentCard | undefined {
    card_number = card_number.replace(/\D/g, "");

    const card = cards.find(card => card.pattern.test(card_number));

    return card;
  },

  cardNumber: function (value: string): string {
    // Remove non-digit characters
    value = value.replace(/\D/g, "");

    // Get card type
    const card = this.getCardFromNumber(value);

    // Remove extra characters
    let limit: number;
    if (card) {
      limit = card.length[card.length.length - 1];
    } else {
      limit = 16;
    }
    value = value.slice(0, limit || 16);

    // Format Value
    if (card) {
      if (card.type === "american_express" || card.type === "diners") {
        const matches: RegExpMatchArray | null = value.match(card.format);
        // first value represents input
        value = matches?.slice(1).join(" ") || "";
      } else {
        value = (value.match(card.format) || []).join(" ");
      }
    } else if (!card && parseInt(value, 10) >= 1) {
      value = (value.match(DEFAULT_FORMAT) || []).join(" ");
    }

    return value;
  },

  expiry: function (value: string): string {
    // Remove non-digit characters
    value = value.replace(/\D/g, "");

    if (value.length > 2) {
      value = value.slice(0, 6);
      value = (value.match(/(\d{1,2})(\d{1,4})/) || []).join(" / ");
      value = value.substr(value.indexOf(" ") + 3);
    }

    return value;
  },

  expiryToDate: function (value: string): string | Error {
    if (value.length < 5 || value.length > 6) {
      return new Error("Invalid Expiry Date");
    }

    const day = "01";
    let month = value.substring(4);
    const year = value.substring(0, 4);

    if (month.length === 1) {
      month = `0${month}`;
    }

    const expiry_date = [year, month, day].join("-");

    return expiry_date;
  },

  passengerFormDate: function (value: string): string {
    let f_value = "";
    // Remove non-digit characters
    value = value.replace(/\D/g, "");

    if (value.length === 1) {
      if (parseInt(value) > 3) {
        f_value = "0" + value;
      } else {
        f_value = value;
      }
    }

    if (value.length === 2) {
      f_value = value;
    }

    if (value.length === 3 || value.length === 4) {
      const matches = value.match(/.{1,2}/g) || [];

      if (value.length === 3) {
        // if second value is bigger than 1, add a zero in front of it
        // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        if (matches.length >= 1 && parseInt(matches[1]) > 1) {
          matches[1] = "0" + matches[1];
        }
      }

      f_value = matches.join(" / ");
    }

    if (value.length > 4) {
      f_value = (value.match(/(\d{1,2})(\d{1,2})?(\d{1,4})?/) || []).join(
        " / "
      );
      f_value = f_value.substr(f_value.indexOf(" ") + 3);
    }

    return f_value;
  },

  passengerFormDateToDate: function (
    value: string | undefined
  ): string | Error {
    if (!value) {
      value = "";
    }

    if (value.length !== 14) {
      return new Error("Invalid Date of Birth");
    }
    const parts = value.split(" / ");

    const day = parts[0];
    const month = parts[1];
    const year = parts[2];

    const dob_date = [year, month, day].join("-");

    return dob_date;
  },

  zip: function (country_code: string, value: string): string {
    const british_rules = [
      /[^0-9A-Z]/,
      /([A-Z0-9]{1,4})?([0-9][A-Z]{2})$/,
      " "
    ] as const;
    const digits_only_rules = [/[^0-9]/g, /([0-9]*)/, ""] as const;

    const rules: Record<string, readonly [RegExp, RegExp, string]> = {
      AUS: digits_only_rules,
      BRA: [/[^0-9]/, /([0-9]{1,5})([0-9]{1,3})?/, "-"],
      CAN: [/[^0-9A-Z]/, /([A-Z0-9]{1,3})([A-Z0-9]{1,3})?/, " "],
      CHE: digits_only_rules,
      DEU: digits_only_rules,
      ESP: digits_only_rules,
      FRA: digits_only_rules,
      GBR: british_rules,
      GGY: british_rules,
      IMN: british_rules,
      ITA: digits_only_rules,
      JEY: british_rules,
      JPN: [/[^0-9]/, /([0-9]{1,3})([0-9]{1,4})?/, "-"],
      MEX: digits_only_rules,
      NLD: [/[^0-9A-Z]/, /([0-9]{1,4})([A-Z]{1,2})?/, " "],
      USA: [/[^0-9]/, /([0-9]{1,5})([0-9]{1,4})?/, "-"]
    };

    const local_rules = rules[country_code];
    if (local_rules) {
      const matches = value
        .toUpperCase()
        .replace(local_rules[0], "")
        .match(local_rules[1]);
      if (matches) {
        return matches
          .filter(m => m !== undefined)
          .slice(1)
          .join(local_rules[2]);
      }
    }

    return value;
  }
};

export default formatter;
